import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Radio, RefreshCw, Clock } from 'lucide-react';
import MedalTally from '@/components/MedalTally';
import { useCompetitions, useLiveScores, useMedalTally, useScoreSSE, type ScoreEntry } from '@/hooks/useApi';

const STATUS_COLORS: Record<string, string> = {
  notStarted: 'bg-gray-200 text-gray-700',
  live: 'bg-red-100 text-red-700',
  completed: 'bg-green-100 text-green-700',
  upcoming: 'bg-yellow-100 text-yellow-700',
};

export default function LiveScoresPage() {
  const { t } = useTranslation();
  const [selectedCompetition, setSelectedCompetition] = useState<string | undefined>();
  const [sseScores, setSseScores] = useState<ScoreEntry[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const esRef = useRef<EventSource | null>(null);

  const { data: competitions } = useCompetitions();
  const { data: polledScores } = useLiveScores(selectedCompetition);
  const { data: medals } = useMedalTally();

  // Try SSE first, fall back to polling
  const onSSEMessage = useCallback((event: { scores?: ScoreEntry[] }) => {
    if (event.scores && Array.isArray(event.scores)) {
      setSseScores(event.scores);
      setLastUpdate(new Date());
    }
  }, []);

  const connectSSE = useScoreSSE(onSSEMessage);

  useEffect(() => {
    const es = connectSSE();
    esRef.current = es;
    return () => { es.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use SSE data if available, otherwise polled data
  const scores = sseScores.length > 0 ? sseScores : (Array.isArray(polledScores) ? polledScores : []);
  const filteredScores = selectedCompetition
    ? scores.filter((s) => s.competitionId === selectedCompetition)
    : scores;

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex items-center gap-3 mb-2">
        <h1 className="section-title">{t('scores.title')}</h1>
        <span className="flex items-center gap-1 text-red-500 text-sm font-medium">
          <Radio className="w-4 h-4 animate-pulse" /> {t('scores.autoRefresh')}
        </span>
      </div>
      <p className="section-subtitle">{t('scores.subtitle')}</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Scores */}
        <div className="lg:col-span-2">
          {/* Competition Filter */}
          <div className="mb-6">
            <label className="text-sm font-medium text-text-secondary block mb-2">
              {t('scores.selectCompetition')}
            </label>
            <select
              value={selectedCompetition || ''}
              onChange={(e) => setSelectedCompetition(e.target.value || undefined)}
              className="w-full md:w-auto px-4 py-2 border border-border rounded-theme bg-bg-card text-text-primary"
            >
              <option value="">{t('common.all')}</option>
              {competitions?.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Score Table */}
          <div className="card overflow-x-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{t('scores.result')}</h2>
              <div className="flex items-center gap-2 text-xs text-text-secondary">
                <Clock className="w-3 h-3" />
                <span>{t('common.lastUpdated')}: {lastUpdate.toLocaleTimeString()}</span>
                <RefreshCw className="w-3 h-3 animate-spin" />
              </div>
            </div>

            {filteredScores.length > 0 ? (
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b-2 border-primary/20">
                    <th className="py-3 px-2 text-sm font-medium text-text-secondary">{t('scores.rank')}</th>
                    <th className="py-3 px-2 text-sm font-medium text-text-secondary">{t('scores.athlete')}</th>
                    <th className="py-3 px-2 text-sm font-medium text-text-secondary">{t('scores.township')}</th>
                    <th className="py-3 px-2 text-sm font-medium text-text-secondary">{t('scores.result')}</th>
                    <th className="py-3 px-2 text-sm font-medium text-text-secondary">{t('common.filter')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredScores.map((score) => (
                    <tr key={score.id} className="border-b border-border/50 hover:bg-[var(--card-hover)]">
                      <td className="py-3 px-2 font-bold">{score.rank}</td>
                      <td className="py-3 px-2">{score.athleteName}</td>
                      <td className="py-3 px-2 text-text-secondary">{score.township}</td>
                      <td className="py-3 px-2 font-mono font-medium">{score.result}</td>
                      <td className="py-3 px-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[score.status]}`}>
                          {t(`scores.status.${score.status}`)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12 text-text-secondary">{t('scores.noResults')}</div>
            )}

            {selectedCompetition && (
              <div className="mt-4">
                <Link to={`/scores/${selectedCompetition}`} className="text-primary text-sm font-medium">
                  {t('common.viewDetails')}
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Right: Medal Tally */}
        <div>
          <div className="card">
            <h2 className="text-lg font-bold mb-4">{t('scores.medalTally')}</h2>
            <MedalTally data={Array.isArray(medals) ? medals : []} compact />
          </div>
        </div>
      </div>
    </div>
  );
}
